import { useCanvasStore, type AwaitingInput } from "@/stores/canvas-store";
import {
  useCompanionStore,
  type ActiveExecution,
} from "@/stores/companion-store";
import {
  useConversationStore,
  type ConversationMessage,
} from "@/stores/conversation-store";
import { useInputBarTarget } from "@/stores/input-bar-target-store";

/** Same bound the backend applies to agent predictions. */
const MAX_PREDICTION_LENGTH = 200;

/**
 * The prediction carried by the conversation's latest message. A prediction
 * is valid only while its assistant message is the latest one. `undefined`
 * means the store holds no message for the conversation at all.
 */
export function latestMessagePrediction(
  messages: readonly ConversationMessage[],
  conversationId: string,
): string | null | undefined {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]!;
    if (message.conversationId !== conversationId || message.activityKind) {
      continue;
    }
    // Native sub-agent partial rows stream with a conversationId.
    return message.role === "assistant" && !message.isStreaming
      ? (message.nextPrompt ?? null)
      : null;
  }
  return undefined;
}

/** Comma-joined ids so a store selector returns a stable primitive. */
function conversationExecutionIds(
  executions: ReadonlyMap<string, ActiveExecution>,
  conversationId: string,
): string {
  const ids: string[] = [];
  for (const execution of executions.values()) {
    if (execution.conversationId === conversationId) ids.push(execution.id);
  }
  return ids.join(",");
}

// "paused" is deliberately excluded: it's the user's own Stop
// (execution_stop_services.pause_execution), it never resumes — the next
// prompt starts a new execution — and the paused entry can sit in the
// executions map for hours until the WebSocket reconnects. Every other
// frontend "is this execution live" check excludes it too (e.g.
// use-conversation-frame-running-state.ts, stop-agent-session.ts,
// companion-store.ts).
const ACTIVE_STATUSES: ReadonlySet<ActiveExecution["status"]> = new Set([
  "queued",
  "running",
  "waiting_approval",
]);

/** A turn is in flight: any earlier prediction is stale. */
export function conversationHasActiveExecution(
  executions: ReadonlyMap<string, ActiveExecution>,
  conversationId: string,
): boolean {
  for (const execution of executions.values()) {
    if (
      execution.conversationId === conversationId &&
      ACTIVE_STATUSES.has(execution.status)
    ) {
      return true;
    }
  }
  return false;
}

/** Rule C: the first option of a pending agent question in the conversation. */
export function pendingQuestionPrediction(
  awaitingInputs: ReadonlyMap<string, AwaitingInput[]>,
  executionIds: string,
): string | null {
  if (!executionIds) return null;
  const ids = new Set(executionIds.split(","));
  for (const list of awaitingInputs.values()) {
    for (const awaiting of list) {
      if (!ids.has(awaiting.executionId)) continue;
      // Sanitised like the backend's agent predictions, since Tab inserts it:
      // control, format and bidi characters removed, then a single line.
      const label = awaiting.options[0]?.label
        .replace(/(?!\s)\p{C}/gu, "")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, MAX_PREDICTION_LENGTH);
      if (label) return label;
    }
  }
  return null;
}

/**
 * The routed conversation's most likely next user message, in priority order:
 * the latest assistant message's agent prediction; the picked row's
 * pending_next_prompt while the store has no messages for the chat; the
 * first option of a pending agent question; otherwise null. While a turn is
 * active only the question fallback applies. A caller that already shows the
 * question passes null instead. Selectors only.
 */
export function usePredictedPrompt(conversationId: string | null): string | null {
  const stored = useConversationStore((state) =>
    conversationId
      ? latestMessagePrediction(state.messages, conversationId)
      : undefined,
  );
  const picked = useInputBarTarget((state) =>
    conversationId && state.target?.conversationId === conversationId
      ? (state.target.pendingNextPrompt ?? null)
      : null,
  );
  const executionIds = useCompanionStore((state) =>
    conversationId
      ? conversationExecutionIds(state.executions, conversationId)
      : "",
  );
  const active = useCompanionStore((state) =>
    conversationId
      ? conversationHasActiveExecution(state.executions, conversationId)
      : false,
  );
  const question = useCanvasStore((state) =>
    pendingQuestionPrediction(state.awaitingInputs, executionIds),
  );
  if (!conversationId) return null;
  if (!active && stored) return stored;
  if (!active && stored === undefined && picked) return picked;
  return question;
}
